"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import ChatInterface from "@/components/chat/ChatInterface"
import { ChatSidebar } from "@/components/chat/ChatSidebar"
import { ProjectSettingsDialog } from "@/components/chat/ProjectSettingsDialog"
import { Button } from "@/components/ui/button"
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar"
import { BottomTabBar } from "@/components/BottomTabBar"
import { useAuth } from "@/hooks/useAuth"
import { useSessions } from "@/hooks/useSessions"
import { useProjects } from "@/hooks/useProjects"
import { GlobalContextProvider } from "@/app/context/GlobalContext"
import { Message } from "@/components/chat/types"
import { AgentSettingsDialog } from "@/components/settings/Settings"
import { AgentProfile, getProfile } from "@/services/boardService"

export default function ChatPage() {
  const { isAuthenticated, signIn } = useAuth()

  if (!isAuthenticated) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <p className="text-4xl">サインインしてください</p>
        <Button onClick={() => signIn()}>サインイン</Button>
      </div>
    )
  }

  return (
    <GlobalContextProvider>
      <NamingRitualGate />
      <ChatPageContent />
    </GlobalContextProvider>
  )
}

/** 命名の儀式（バッチA2）: 新規メンバーの初回体験。

    プロファイルは個人軸（tenantId×userId）なので、招待されたばかりのメンバーは
    agentName が空 → チュートリアル（命名→インタビュー）を自動で開く。
    取得失敗時は何も出さない（会話を止めない・安全側）。 */
function NamingRitualGate() {
  const { user } = useAuth()
  const idToken = user?.id_token || ""
  const [profile, setProfile] = useState<AgentProfile | null>(null)
  const [show, setShow] = useState(false)

  useEffect(() => {
    if (!idToken) return
    getProfile(idToken)
      .then((p) => {
        setProfile(p)
        if (!p.agentName) setShow(true)
      })
      .catch(() => {
        /* プロファイル取得失敗でもチャットは使える */
      })
  }, [idToken])

  if (!show) return null
  return (
    <AgentSettingsDialog
      idToken={idToken}
      profile={profile}
      avatarUrl=""
      tutorial
      onClose={() => setShow(false)}
    />
  )
}

function ChatPageContent() {
  const {
    sessions,
    currentSessionId,
    messages,
    setMessages,
    isLoadingMore,
    hasMore,
    saveError,
    loadSessions,
    loadMoreSessions,
    selectSession,
    startNewChat,
    createNewSession,
    saveMessages,
    deleteSession,
  } = useSessions()

  const {
    projects,
    currentProjectId,
    currentProject,
    selectProject,
    createProject,
    updateProject,
    deleteProject,
    uploadKnowledgeFile,
    removeKnowledgeFile,
  } = useProjects()

  const [projectDialogOpen, setProjectDialogOpen] = useState(false)
  const [editingProject, setEditingProject] = useState<boolean>(false)

  // Ref to abort active stream before session/navigation changes
  const abortStreamRef = useRef<(() => void) | null>(null)

  // Wrap selectSession to abort active stream first
  const handleSessionSelect = useCallback(
    (sessionId: string) => {
      abortStreamRef.current?.()
      selectSession(sessionId)
    },
    [selectSession]
  )

  // Wrap startNewChat to abort active stream first
  const handleStartNewChat = useCallback(() => {
    abortStreamRef.current?.()
    startNewChat()
  }, [startNewChat])

  // Handle project selection — reload sessions filtered by project
  const handleProjectSelect = useCallback(
    (projectId: string | null) => {
      abortStreamRef.current?.()
      selectProject(projectId)
      startNewChat()
      if (projectId && projectId !== "__unassigned__") {
        loadSessions(projectId)
      } else if (projectId === "__unassigned__") {
        loadSessions("__unassigned__")
      } else {
        loadSessions()
      }
    },
    [selectProject, startNewChat, loadSessions]
  )

  // Open dialog in create mode
  const handleNewProject = useCallback(() => {
    setEditingProject(false)
    setProjectDialogOpen(true)
  }, [])

  // Open dialog in edit mode
  const handleProjectSettings = useCallback(
    (projectId: string) => {
      selectProject(projectId)
      setEditingProject(true)
      setProjectDialogOpen(true)
    },
    [selectProject]
  )

  // Called by ChatInterface when the first message is sent and no session exists yet
  const handleNeedSession = useCallback(
    async (firstMessage: string): Promise<string> => {
      const title = firstMessage.slice(0, 50) + (firstMessage.length > 50 ? "..." : "")
      const projectId =
        currentProjectId && currentProjectId !== "__unassigned__"
          ? currentProjectId
          : undefined
      return createNewSession(title, projectId)
    },
    [createNewSession, currentProjectId]
  )

  // Called by ChatInterface after streaming completes
  const handleMessagesChange = useCallback(
    (msgs: Message[], sessionId: string) => {
      saveMessages(msgs, sessionId)
    },
    [saveMessages]
  )

  // Derive current chat title from sessions list
  const currentChatTitle = useMemo(() => {
    if (!currentSessionId) return undefined
    const session = sessions.find((s) => s.sessionId === currentSessionId)
    return session?.title
  }, [currentSessionId, sessions])

  // Handle project creation — auto-select the new project, then switch dialog to edit mode
  const handleCreateProject = useCallback(
    async (data: Parameters<typeof createProject>[0]) => {
      const projectId = await createProject(data)
      handleProjectSelect(projectId)
      setEditingProject(true)
      return projectId
    },
    [createProject, handleProjectSelect]
  )

  // Handle project deletion — reset to all projects view
  const handleDeleteProject = useCallback(
    async (projectId: string) => {
      await deleteProject(projectId)
      handleProjectSelect(null)
    },
    [deleteProject, handleProjectSelect]
  )

  return (
    <SidebarProvider>
      <ChatSidebar
        sessions={sessions}
        currentSessionId={currentSessionId}
        onSessionSelect={handleSessionSelect}
        onNewChat={handleStartNewChat}
        onDeleteSession={deleteSession}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        onLoadMore={loadMoreSessions}
        projects={projects}
        currentProjectId={currentProjectId}
        onProjectSelect={handleProjectSelect}
        onNewProject={handleNewProject}
        onProjectSettings={handleProjectSettings}
      />
      <SidebarInset>
        <ChatInterface
          sessionId={currentSessionId}
          messages={messages}
          setMessages={setMessages}
          onNewChat={handleStartNewChat}
          onMessagesChange={handleMessagesChange}
          abortStreamRef={abortStreamRef}
          onNeedSession={handleNeedSession}
          customInstructions={currentProject?.customInstructions}
          knowledgeFiles={currentProject?.knowledgeFiles}
          projectName={currentProject?.name}
          chatTitle={currentChatTitle}
          notice={saveError}
        />
        {/* モバイル専用の下部タブバー（チャット/ボード切替）。SidebarInset の flex 末尾に
            置くことで、ChatInterface 側で確保した高さ（h-dvh からタブバー分を差し引き済み）
            の下にちょうど収まり、コンポーザーがタブバーに隠れない。 */}
        <BottomTabBar />
      </SidebarInset>
      <ProjectSettingsDialog
        open={projectDialogOpen}
        onOpenChange={setProjectDialogOpen}
        project={editingProject ? currentProject : null}
        onCreateProject={handleCreateProject}
        onUpdateProject={updateProject}
        onDeleteProject={handleDeleteProject}
        onUploadKnowledgeFile={uploadKnowledgeFile}
        onRemoveKnowledgeFile={removeKnowledgeFile}
      />
    </SidebarProvider>
  )
}
